import { FC } from 'react'
import { Droppable } from 'react-beautiful-dnd'
import { Row } from '@mobileTypes'
import { Add as AddIcon } from '@mui/icons-material'
import { Box, Button, Paper, Typography } from '@mui/material'

import { RowContainer } from './RowContainer'

interface ConfiguredRowsPanelProps {
  rows: Row[]
  onAddRow: () => void
  onDeleteRow: (rowIndex: number) => void
  onEditElement: (rowIndex: number, elementIndex: number) => void
  onDeleteElement: (rowIndex: number, elementIndex: number) => void
}

export const ConfiguredRowsPanel: FC<ConfiguredRowsPanelProps> = ({
  rows,
  onAddRow,
  onDeleteRow,
  onEditElement,
  onDeleteElement,
}) => {
  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Typography variant='h6'>Configured Rows</Typography>
          <Typography variant='caption' color='text.secondary'>
            {rows.length} row(s) configured
          </Typography>
        </div>
        <Button variant='outlined' size='small' startIcon={<AddIcon />} onClick={onAddRow}>
          Add Row
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Droppable droppableId='ROWS' type='ROWS'>
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                minHeight: 200,
                ...(snapshot.isDraggingOver && {
                  backgroundColor: theme => theme.palette.action.hover,
                }),
              }}
            >
              {rows.length === 0 && (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: theme => `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    No rows configured
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Click "Add Row" to start
                  </Typography>
                </Box>
              )}
              {rows.map((row, index) => (
                <RowContainer
                  key={`row-${index}`}
                  row={row}
                  rowIndex={index}
                  onDeleteRow={() => onDeleteRow(index)}
                  onEditElement={onEditElement}
                  onDeleteElement={onDeleteElement}
                />
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Box>
    </Paper>
  )
}
